<template>
    <vue-scroll>
        <div class="page-header">
            <h1>Communities</h1>
        </div>
        <div class="float-left">
            <el-input
                placeholder="Search Communities..."
                style="width: 250px"
                v-model="filter.search"
            />
        </div>
        <el-table row-key="id" :data="paginatedData">
            <el-table-column label="Name" prop="name" />
            <el-table-column prop="address.city" label="City and State">
                <template slot-scope="{ row }" v-if="row.address"
                    >{{ row.address.city }}, {{ row.address.state }}
                </template>
            </el-table-column>
            <el-table-column prop="logo" label="Logo">
                <template slot-scope="{ row }">
                    <img
                        :src="row.formatted_logo.url"
                        alt="Logo"
                        v-if="row.formatted_logo"
                        width="100"
                    />
                </template>
            </el-table-column>
            <el-table-column label="Status">
                <template slot-scope="{ row }">{{
                    row.suspended ? "Suspended" : "Active"
                }}</template>
            </el-table-column>
            <el-table-column prop="actions" width="400px">
                <template slot-scope="{ row }">
                    <el-button-group>
                        <el-button
                            @click="view(row)"
                            icon="mdi mdi-eye"
                            size="small"
                            type="success"
                            >View</el-button
                        >
                        <el-button
                            size="small"
                            type="primary"
                            icon="mdi mdi-image"
                            @click="editLogo(row)"
                            >Update Logo
                        </el-button>
                        <el-button
                            @click="loginToCommunity(row)"
                            icon="mdi mdi-login"
                            size="small"
                            class="el-button--corwin-green"
                            >Log In
                        </el-button>
                    </el-button-group>
                </template>
            </el-table-column>
        </el-table>
        <div class="float-right mt-10">
            <el-button
                @click="downloadInvoices()"
                icon="mdi mdi-download"
                round
                type="success"
                >Download Invoices
            </el-button>
            <el-button
                @click="downloadCalendarFiles()"
                icon="mdi mdi-download"
                round
                type="primary"
                v-if="!$store.state.config.settings.disable_order_download"
            >
                Download Calendar Files
            </el-button>
        </div>
        <div class="clearfix"></div>
        <div class="mt-10" v-if="pageCount >= 1">
            <el-pagination
                class="pull-right"
                v-if="pagination && pageCount > 1"
                background
                :page-size="pagination.per_page"
                :current-page.sync="pagination.current_page"
                :total="dataCount"
                @current-change="changePage"
                @size-change="handleSizeChange"
            />
            <div class="clearfix"></div>
        </div>
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="viewDialogVisible"
            title="View Community"
            width="80%"
        >
            <template v-if="viewingCommunity">
                <table class="w-full">
                    <tbody>
                        <tr>
                            <td class="bold" style="width: 100px">Name</td>
                            <td>{{ viewingCommunity.name }}</td>
                        </tr>
                        <tr>
                            <td class="bold">Address</td>
                            <td>{{ viewingCommunity.address.full }}</td>
                        </tr>
                    </tbody>
                </table>
                <h3>Calendars</h3>
                <el-table class="mt-20" :data="viewingCommunity.locations.data">
                    <el-table-column prop="name" label="Calendar Unit Name" />
                    <el-table-column prop="code" label="Calendar Unit Code" />
                    <el-table-column
                        prop="start_date"
                        label="Start Date"
                        :formatter="formatterMonthYear"
                    />
                    <el-table-column prop="calendar_format" label="Format">
                        <template slot-scope="{ row }"
                            >{{
                                row.calendar_format === CalendarFormats.MONTHLY
                                    ? "Monthly"
                                    : "Weekly"
                            }}
                        </template>
                    </el-table-column>
                </el-table>
                <h3>Users</h3>
                <communityUsers
                    :community="viewingCommunity"
                    :corporation="current_corporation"
                    :form="form"
                />
            </template>
            <div slot="footer" class="dialog-footer">
                <el-button @click="viewDialogVisible = false">Close</el-button>
            </div>
        </el-dialog>
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="editLogoDialogVisible"
            title="Update Logo"
            width="500px"
        >
            <template v-if="viewingCommunity">
                <el-form>
                    <edit-logo
                        :community="viewingCommunity"
                        :corporation="current_corporation"
                        :form="form"
                    />
                </el-form>
            </template>
            <div slot="footer" class="dialog-footer">
                <el-button @click="editLogoDialogVisible = false"
                    >Cancel</el-button
                >
                <el-button
                    @click="saveLogo()"
                    type="primary"
                    icon="mdi mdi-content-save"
                    :loading="form.busy"
                >
                    Save Changes
                </el-button>
            </div>
        </el-dialog>
        <el-dialog
            title="Download Calendar Files"
            :close-on-click-modal="false"
            :visible.sync="downloadCalendarsDialogVisible"
            width="500px"
        >
            <el-form label-width="150px">
                <el-form-item
                    label="Year/Month"
                    label-width="150px"
                    required
                    :class="downloadCalendarsForm.state('month')"
                >
                    <el-date-picker
                        style="width: 100%"
                        v-model="downloadCalendarsForm.year_month"
                        v-validate="'required'"
                        @change="onChangeDownloadCalendarMonth"
                        name="month"
                        type="month"
                    />
                    <form-feedback
                        :form="downloadCalendarsForm"
                        field="month"
                    />
                </el-form-item>
                <el-form-item
                    label="Format"
                    required
                    :class="downloadCalendarsForm.state('format')"
                >
                    <el-radio-group
                        v-model="downloadCalendarsForm.format"
                        name="format"
                        v-validate="'required'"
                    >
                        <el-radio :label="CalendarDownloadFormats.PDF"
                            >Pdf</el-radio
                        >
                        <el-radio :label="CalendarDownloadFormats.JPG"
                            >Jpg</el-radio
                        >
                    </el-radio-group>
                    <form-feedback
                        :form="downloadCalendarsForm"
                        field="format"
                    />
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="downloadCalendarsDialogVisible = false"
                    >Cancel</el-button
                >
                <el-button
                    @click="confirmDownloadCalendarFiles()"
                    type="primary"
                    icon="mdi mdi-download"
                    :loading="downloadCalendarsForm.busy"
                    >Download
                </el-button>
            </div>
        </el-dialog>
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="downloadInvoicesDialogVisible"
            title="Download Invoices"
            width="500px"
        >
            <el-form label-width="150px">
                <el-form-item
                    :class="downloadInvoicesForm.state('month')"
                    label="Year/Month"
                    label-width="150px"
                    required
                >
                    <el-date-picker
                        @change="onChangeDownloadInvoicesMonth"
                        data-vv-scope="downloadInvoicesForm"
                        name="month"
                        style="width: 100%"
                        type="month"
                        v-model="downloadInvoicesForm.year_month"
                        v-validate="'required'"
                    />
                    <form-feedback :form="downloadInvoicesForm" field="month" />
                </el-form-item>
            </el-form>
            <div class="dialog-footer" slot="footer">
                <el-button @click="downloadInvoicesDialogVisible = false"
                    >Cancel</el-button
                >
                <el-button
                    :loading="downloadInvoicesForm.busy"
                    @click="confirmDownloadInvoices()"
                    icon="mdi mdi-download"
                    type="primary"
                    >Download
                </el-button>
            </div>
        </el-dialog>
    </vue-scroll>
</template>

<script>
import * as AccountTypes from "@/constants/account-types";
import * as CalendarDownloadFormats from "@/constants/calendar-download-formats";
import * as CalendarFormats from "@/constants/calendar-formats";
import community_mixin from "@/mixins/community";
import paginator from "@/mixins/frontend-paginator";
import formatters from "@/mixins/table/formatters";
import * as Mutations from "@/store/constants/mutations";
import FileUtils from "@/utils/file";
import { mapState } from "vuex";
import CommunityUsers from "../community/CommunityUsers";
import EditLogo from "../community/EditLogo";

export default {
    mixins: [paginator, formatters, community_mixin],
    components: {
        CommunityUsers,
        EditLogo
    },
    data() {
        return {
            AccountTypes,
            CalendarFormats,
            CalendarDownloadFormats,
            viewDialogVisible: false,
            editLogoDialogVisible: false,
            downloadCalendarsDialogVisible: false,
            downloadInvoicesDialogVisible: false,
            form: new Form({
                logo: null,
                no_logo: false
            }),
            logo_src: null,
            editingItem: null,
            dataKey: "filteredItems",
            filter: {
                search: ""
            },
            viewingCommunity: null,
            downloadCalendarsForm: new Form({
                year_month: null,
                year: null,
                month: null,
                format: CalendarDownloadFormats.PDF
            }),
            downloadInvoicesForm: new Form({
                year_month: null,
                year: null,
                month: null
            })
        };
    },
    computed: {
        ...mapState({
            items: (state) => state.communities,
            corporations: (state) => state.corporations,
            newsletterTypes: (state) => state.lists.newsletter_types.data
        }),
        ...mapState(["current_corporation"]),
        hasNewsletter() {
            return _.includes(
                [
                    AccountTypes.CALENDARS_AND_NEWSLETTER,
                    AccountTypes.NEWSLETTER_ONLY
                ],
                this.form.account_type
            );
        },
        hasCalendar() {
            return _.includes(
                [
                    AccountTypes.CALENDARS_AND_NEWSLETTER,
                    AccountTypes.CALENDARS_ONLY
                ],
                this.form.account_type
            );
        },
        filteredItems() {
            return this.items.data.filter((community) => {
                if (!window.Utils.emptyOrNull(this.filter.search)) {
                    return (
                        (community.name &&
                            community.name
                                .toLowerCase()
                                .includes(this.filter.search.toLowerCase())) ||
                        community.slug
                            .toLowerCase()
                            .includes(this.filter.search.toLowerCase())
                    );
                }
                return true;
            });
        }
    },
    mounted() {
        this.downloadCalendarsForm.setVeeErrors(this.veeErrors);
        this.downloadInvoicesForm.setVeeErrors(
            this.veeErrors,
            "downloadInvoicesForm"
        );
    },
    methods: {
        onChangeLogo(e) {
            window.Utils.getFileImageData(e.currentTarget).then((response) => {
                this.logo_src = response;
                this.form.logo = e.target.files[0];
            });
        },
        view(community) {
            this.viewingCommunity = community;
            this.viewDialogVisible = true;
        },
        editLogo(community) {
            this.viewingCommunity = community;
            this.form.no_logo = community.no_logo;
            this.editLogoDialogVisible = true;
        },
        saveLogo() {
            App.putData(
                `/web-api/community/${this.viewingCommunity.id}?include=formatted_logo,logo`,
                this.form
            ).then((response) => {
                this.editLogoDialogVisible = false;
                this.$notify.success({
                    message: "Changes have been saved."
                });
                this.$store.commit(Mutations.UPDATE_COMMUNITY, {
                    id: this.viewingCommunity.id,
                    no_logo: response.data.no_logo,
                    logo: response.data.logo,
                    formatted_logo: response.data.formatted_logo
                });
            });
        },
        downloadCalendarFiles() {
            this.downloadCalendarsDialogVisible = true;
        },
        confirmDownloadCalendarFiles() {
            this.$validator.validate(null).then((valid) => {
                if (!valid) {
                    return;
                }
                this.downloadCalendarsForm.startProcessing();
                axios
                    .get("/web-api/corporation/download/calendar", {
                        params: {
                            year: this.downloadCalendarsForm.year,
                            month: this.downloadCalendarsForm.month,
                            format: this.downloadCalendarsForm.format
                        }
                    })
                    .then(() => {
                        this.$notify.success({
                            message: `Your download has been queued. We'll notify you through email when your calendars are ready for download.`
                        });
                        this.downloadCalendarsDialogVisible = false;
                    })
                    .then(() => {
                        this.downloadCalendarsForm.finishProcessing();
                    });
            });
        },
        onChangeDownloadCalendarMonth(e) {
            if (!e) {
                this.downloadCalendarsForm.year = null;
                this.downloadCalendarsForm.month = null;
                return;
            }
            this.downloadCalendarsForm.year = e.getFullYear();
            this.downloadCalendarsForm.month = e.getMonth() + 1;
        },
        downloadInvoices() {
            this.downloadInvoicesDialogVisible = true;
        },
        onChangeDownloadInvoicesMonth(e) {
            if (!e) {
                this.downloadInvoicesForm.year = null;
                this.downloadInvoicesForm.month = null;
                return;
            }
            this.downloadInvoicesForm.year = e.getFullYear();
            this.downloadInvoicesForm.month = e.getMonth() + 1;
        },
        confirmDownloadInvoices() {
            this.$validator.validate("downloadInvoicesForm.*").then((valid) => {
                if (!valid) {
                    return;
                }
                this.downloadInvoicesForm.startProcessing();
                axios
                    .get("/web-api/corporation/download/invoice", {
                        responseType: "blob",
                        params: {
                            year: this.downloadInvoicesForm.year,
                            month: this.downloadInvoicesForm.month
                        }
                    })
                    .then((response) => {
                        FileUtils.downloadFileFromBlobResponse(response);
                        this.downloadInvoicesDialogVisible = false;
                    })
                    .then(() => {
                        this.downloadInvoicesForm.finishProcessing();
                    });
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.community-locations {
    .community-locations__item {
        &:not(:first-of-type) {
            margin-top: 1rem;
        }
    }
}
</style>
